import React from "react";
import { withTranslation } from "react-i18next";
import { compose } from "react-recompose";
import { connect } from "react-redux";
import { makeOppositeShade } from "../../utils/colorService";
import withRouter from "../../HOC/withRouter";
import get from "lodash/get";
import { PieChart } from "react-minimal-pie-chart";
import dayjs from "dayjs";
import PlusSignIcon from "../../static/images/PlusSignIcon.svg";
import ViewProfileIcon from "../../static/images/ViewProfileIcon.svg";
import ArrowRightIcon from "../../static/images/ArrowRightIcon.svg";
import { getTaskImage, BOOLEAN_TASK_TEMPLATES } from "../../utils/task";
import AddTPButton from "../../components/svgs/AddTPButton";
import TaskFulfilled from "../../components/svgs/TaskFulfilled";
import TaskNotFulfilled from "../../components/svgs/TaskNotFulfilled";
import TaskItems from "./TaskItems";
import WheelOfFortune from "./WheelOfFortune";
import IconMoreInfo from "../../components/svgs/MoreInfo";
import ReactTooltip from "react-tooltip";
import "./styles.scss";
import TeamChallenge from "../../components/svgs/TeamChallenge";
import classNames from "classnames";

const TaskView = ({
  t,
  navigate,
  task,
  ended,
  profileId,
  isFullscreen,
  TPNames,
  client,
  modalVisible = false,

  closeModal = () => {},
}) => {
  const gotoProfile = () => {
    if (modalVisible) {
      closeModal();
    }
    navigate("/profile");
  };

  const viewProfile = () => {
    if (modalVisible) {
      closeModal();
    }
    navigate(`/view-profile/${profileId}`);
  };

  const gotoTouchpoint = id => {
    if (modalVisible) {
      closeModal();
    }
    navigate(`/touchpoint/${id}`);
  };

  const gotoQuiz = () => {
    if (modalVisible) {
      closeModal();
    }

    //Get selected quizzes which belong to the current challenge and pass them to the standard quiz logic
    const selectedQuizzes = task.quizzes;
    const filteredQuizzes = selectedQuizzes.map(quiz => `${quiz}`);

    navigate(`/quiz`, { state: { filteredQuizzes } });
  };

  const gotoQuizView = id => {
    if (modalVisible) {
      closeModal();
    }
    navigate(`/quiz/${id}`);
  };

  const gotoSurvey = () => {
    if (modalVisible) {
      closeModal();
    }

    //Get selected surveys which belong to the current challenge and pass them to the standard survey logic
    const filteredSurveys = task.surveys;

    navigate(`/survey`, { state: { filteredSurveys } });
  };

  const gotoSurveyView = id => {
    if (modalVisible) {
      closeModal();
    }
    navigate(`/survey/${id}`);
  };

  /* Navigates to the TP create page with optional category ID and challenge description. */
  const goToTPCreate = (categoryId, challengeDescription) => {
    const state = {};
    if (categoryId) {
      state.categoryId = categoryId;
    }
    if (challengeDescription) {
      state.challengeDescription = challengeDescription;
    }
    navigate("/touchpoint/new", { state: Object.keys(state).length !== 0 ? state : null });
  };

  const goToTimeTrial = id => {
    navigate(`/timetrial/${id}`);
  };

  const getQuizCompletion = task => {
    const completionInfo = task.isWorkspaceTask ? get(task, "workspaceQuizCompletion") : get(task, "quizCompletion");
    const completion =
      completionInfo &&
      completionInfo.map(q => {
        const [id, info] = Object.entries(q)[0];
        return { [id]: info.passed };
      });
    return completion;
  };

  const getQuizNames = task => {
    let completionInfo = task.isWorkspaceTask ? get(task, "workspaceQuizCompletion") : get(task, "quizCompletion");
    completionInfo = completionInfo?.map(q => {
      const [, info] = Object.entries(q)[0];
      return info;
    });
    const quizNames = completionInfo && completionInfo.reduce((acc, cur) => ({ ...acc, [cur._id]: cur.name }), {});
    return quizNames;
  };

  const getTeamName = (teamArray, taskGroup) => {
    const team = teamArray?.find(member => member[taskGroup]);
    if (team) {
      return team[taskGroup].trim().replace(/^,|,$/g, ""); // Remove commas at first and last position of team name
    }
    return "Unknown Team";
  };

  const teamChallengeComponent = (
    <div className="team-challenge">
      <div className="task-template" style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
        <div className="info-icon" data-tip data-for={task._id}>
          <IconMoreInfo color={client?.secondaryColor} />
        </div>
      </div>
      <span className="team-additional-info">
        <ReactTooltip
          id={task._id}
          className="tooltip"
          effect="solid"
          type="light"
          place="bottom"
          border={false}
          multiline={true}
        >
          <div className="team-name">{getTeamName(task.team, task.teamTaskGroup)}</div>
          {task?.team?.map(member => (
            <div key={member.id}>{member.name}</div>
          ))}
        </ReactTooltip>
      </span>
    </div>
  );

  const completionInCalculation = task.isWorkspaceTask
    ? task.workspaceCompletionLevel
    : task.teamTaskType === "together"
      ? task.teamsCompletion
      : task.completionLevel;

  let pieChartData = [
    {
      title: "Completed",
      value: completionInCalculation,
      color: client?.secondaryColor,
    },
    {
      title: "Total",
      value: task.quantity < completionInCalculation ? 0 : task.quantity - completionInCalculation,
      color: "#f8f8f8",
    },
  ];

  return (
    <div
      className={classNames("task-container", {
        ended: !!ended,
        workspace: !!task.isWorkspaceTask,
        fullscreen: !!isFullscreen,
      })}
    >
      <div
        className={`task-content ${isFullscreen ? "fullscreen" : ""} ${
          task.template === "wheelOfFortune" ? "wheelOfFortune" : ""
        }`}
      >
        {task.team && task.team.length > 0 && (
          <div className="challenge-type-icon">
            <TeamChallenge
              color={client?.primaryColor}
              color2={client?.secondaryColor}
              color3={client?.primaryTextColor}
              color4={client?.bgColorOppositeShade}
            />
          </div>
        )}
        <div className="task-end-date">
          {task.end
            ? t("tasks.UntilDate", {
                date: dayjs(task.end).format("DD.MM.YYYY"),
              })
            : ""}
        </div>
        {(task.isFulfilled || ended) && (
          <div className={task.isFulfilled ? "fulfillment-icon fulfilled" : "fulfillment-icon not-fulfilled"}>
            {task.isFulfilled ? (
              <TaskFulfilled color={makeOppositeShade(client?.secondaryTextColor)} />
            ) : (
              <TaskNotFulfilled color={client?.secondaryColor} />
            )}
          </div>
        )}
        {!BOOLEAN_TASK_TEMPLATES.has(task.template) && task.quantity > 1 && task.template !== "timeTrial" && (
          <div className="task-fulfillment-data">
            <PieChart className="task-chart" startAngle={-90} radius={40} data={pieChartData} lineWidth={5} rounded />
            <div className="task-fulfillment-details">
              {t("tasks.TaskProgress", {
                completed: task.quantity >= completionInCalculation ? completionInCalculation : task.quantity,
                total: task.quantity,
              })}
            </div>
          </div>
        )}
        {!BOOLEAN_TASK_TEMPLATES.has(task.template) && task.quantity > 1 && task.template === "timeTrial" && (
          <div className="time-trial-data">
            <div className="time-trial-details">
              {t("tasks.TimeTrial", {
                total: task.quantity,
                time: task.seconds,
              })}
            </div>
          </div>
        )}
        {/* <div className="task-headline"> {t(`tasks.${task.template}`, { count: task.quantity })}</div>
        <div className={BOOLEAN_TASK_TEMPLATES.has(task.template) ? 'edit-profile-template' : 'task-template'}> */}
        <div className="task-template">{task.headline}</div>
        {BOOLEAN_TASK_TEMPLATES.has(task.template) && (
          <div className="task-button" onClick={ended || task.booleanCompletion ? viewProfile : gotoProfile}>
            <img
              src={ended || task.booleanCompletion ? ViewProfileIcon : PlusSignIcon}
              alt=""
              className={ended || task.booleanCompletion ? "view" : "plus"}
            />
            <span>
              {ended || task.booleanCompletion ? t("tasks.viewProfileBtnText") : t(`tasks.${task.template}BtnText`)}
            </span>
          </div>
        )}
        <div className="task-decription" dangerouslySetInnerHTML={{ __html: task.description }}></div>
        {task.template === "wheelOfFortune" ? (
          <WheelOfFortune task={task} ended={ended} client={client} />
        ) : (
          <div className="task-footer">
            <span className="task-image">
              {getTaskImage(task.template, makeOppositeShade(client?.secondaryTextColor))}
            </span>
            {task.isWorkspaceTask && (
              <div className="my-task-input">
                {t("tasks.taskContribution", {
                  completionLevel: task.quantity >= task.completionLevel ? task.completionLevel : task.quantity,
                  completionPercentage: Math.round((100 * task.completionLevel) / task.quantity) || 0,
                })}
              </div>
            )}
            {task.teamTaskType === "together" && (
              <div className="my-task-input">
                {t("tasks.teamChallengeTogetherFulfillment", {
                  completionLevel: task.quantity >= task.teamsCompletion ? task.teamsCompletion : task.quantity,
                  completionPercentage: Math.round((100 * task.completionLevel) / task.quantity) || 0,
                })}
                {teamChallengeComponent}
              </div>
            )}
            {task.teamTaskType === "all" && (
              <div className="my-task-input">
                {t("tasks.teamChallengeAllFulfillment", {
                  completionLevel: task.teamsCompletion || 0,
                  completionTeamNeeded: task.teamsCompletionNeeded || 0,
                })}
                {t("tasks.teamMembers")}
                {teamChallengeComponent}
              </div>
            )}
            {task.template === "newTP" && (
              <div
                onClick={() => goToTPCreate(task?.categoryFilter ?? null, task?.description ?? null)}
                className="add-touchpoint-link"
              >
                <AddTPButton color={client?.secondaryColor} color2={makeOppositeShade(client?.bgColor)} />
                <span className="add-tp-text">{t("tasks.newTP", { lblTouchPoint: client?.lblTouchPoint })}</span>
              </div>
            )}
            {task.template === "timeTrial" && (
              <div onClick={() => goToTimeTrial(task?._id)} className="add-touchpoint-link">
                <AddTPButton color={client?.secondaryColor} color2={makeOppositeShade(client?.bgColor)} />
                <span className="add-tp-text">{t("tasks.participateInTimeTrialBtnText")}</span>
              </div>
            )}
            {!!task.touchPoints.length && (
              <TaskItems
                client={client}
                itemCompletion={task.isWorkspaceTask ? task.workspaceTpCompletion : task.tpCompletion}
                itemsNames={TPNames}
                gotoItem={gotoTouchpoint}
              />
            )}
            {task.template === "quizzes" && !!task.quizzes.length && (
              <>
                {task.isFulfilled || task.quizzes.length < 2 || completionInCalculation === task.quantity ? (
                  <TaskItems
                    client={client}
                    itemCompletion={getQuizCompletion(task)}
                    itemsNames={getQuizNames(task)}
                    gotoItem={gotoQuizView}
                  />
                ) : (
                  <div className="task-button" onClick={gotoQuiz}>
                    <span className="quiz">{t("tasks.StartQuizChallenge")}</span>
                    <img src={ArrowRightIcon} alt="" className="arrow" />
                  </div>
                )}
              </>
            )}
            {!!task.surveys.length && (
              <>
                {task.isFulfilled || task.surveys.length < 2 || completionInCalculation === task.quantity ? (
                  <TaskItems
                    client={client}
                    itemCompletion={getQuizCompletion(task)}
                    itemsNames={getQuizNames(task)}
                    gotoItem={gotoSurveyView}
                  />
                ) : (
                  <div className="task-button" onClick={gotoSurvey}>
                    <span className="quiz">{t("tasks.StartSurveyChallenge")}</span>
                    <img src={ArrowRightIcon} alt="" className="arrow" />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
const mapStateToProps = state => ({
  client: state.client.client,
  profile: state.user.profile,
});

export default compose(withRouter, connect(mapStateToProps))(withTranslation()(TaskView));
